import BaseApiClient from "../api/baseApiClient.service";
import type { OrganizationInvitationCheckResponseDTO } from "./invitation.types";
import type { GenericResponse } from "../api/response.types";

/**
 * This service handles checking invitations on the orga invitation page
 */
export class OrgaInvitationService {
	// singleton logic
	private static instance: OrgaInvitationService;
	private static baseApi: BaseApiClient;

	private baseApiSubPath = "/invitation";

	public static getInstance(): OrgaInvitationService {
		if (!OrgaInvitationService.instance) {
			OrgaInvitationService.instance = new OrgaInvitationService();
			OrgaInvitationService.baseApi = BaseApiClient.getInstance();
		}
		return OrgaInvitationService.instance;
	}

	/**
	 * This will check if the invitation is valid
	 * @param invitationId
	 * @returns OrganizationInvitationCheckResponseDTO, rejects if the invitation is invalid
	 */
	public async checkInvitationValidity(
		invitationId: string,
	): Promise<OrganizationInvitationCheckResponseDTO> {
		const response =
			await OrgaInvitationService.baseApi.get<OrganizationInvitationCheckResponseDTO>(
				`${this.baseApiSubPath}/${invitationId}`,
			);

		if (!response.success) {
			return Promise.reject();
		}

		return Promise.resolve(response);
	}

	/**
	 * This will accept an invitation to join an organization
	 * @param invitationId The ID of the invitation to accept
	 * @returns The response from the API
	 */
	public async acceptInvitation(invitationId: string): Promise<void> {
		await OrgaInvitationService.baseApi.post<GenericResponse, null>(
			`${this.baseApiSubPath}/${invitationId}/accept`,
			null,
		);
	}

	/**
	 * Redeems an invitation by setting a password (and optional display name)
	 * This endpoint is intended for users who are not logged in yet.
	 * @param invitationId The ID of the invitation to redeem
	 * @param password The password to set for the new account
	 * @param displayName Optional display name to set for the account
	 */
	public async redeemInvitation(
		invitationId: string,
		password: string,
		displayName?: string,
	): Promise<void> {
		await OrgaInvitationService.baseApi.post<
			GenericResponse,
			{
				password: string;
				displayName?: string;
			}
		>(`${this.baseApiSubPath}/${invitationId}/redeem`, {
			password,
			displayName,
		});
	}
}
